import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
	BillingUnit,
	PriceFilter,
	PricesByProgramQuery,
	usePricesByProgramQuery,
} from '@src/graphqlTypes/graphqlGatewayTypes';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { useDecision } from '@optimizely/react-sdk';
import { PROGRAM_COST, HIDE_TUITION_WHEN_NO_OUT_OF_POCKET } from '@src/constants/featureFlags';
import { logDataDogError } from '@src/utilities/datadog';
import { shouldShowCost } from '@src/constants/cost';

interface PricebookContextInterface {
	hideTuitionWhenNoOutOfPocket: boolean;
	programCost: PricesByProgramQuery['pricesByProgram'][0]['costPerUnit'];
	programCostLoading: boolean;
	programCostUnavailable: boolean;
	showCost: boolean;
}

export const PricebookContext = createContext<PricebookContextInterface | undefined>(undefined);

export const usePricebook = () => {
	const context = useContext(PricebookContext);

	if (context === undefined) {
		throw new Error('usePricebook must be used within PricebookProvider.');
	}

	return context;
};

export interface PricebookProviderProps {
	children: ReactNode;
	academicModel: GetMemberProgramQuery['memberProgram']['studentFacingAcademicPartner']['academicModel'];
	academicPartnerUuid: GetMemberProgramQuery['memberProgram']['studentFacingAcademicPartner']['uuid'];
	programUuid: GetMemberProgramQuery['memberProgram']['uuid'];
}

export const PricebookProvider: React.FC<PricebookProviderProps> = ({
	children,
	academicModel,
	academicPartnerUuid,
	programUuid,
}) => {
	const [costDecision] = useDecision(PROGRAM_COST);
	const hideTuitionWhenNoOutOfPocket =
		costDecision.variationKey === HIDE_TUITION_WHEN_NO_OUT_OF_POCKET;
	const showCost = shouldShowCost(academicModel, costDecision);
	const [programCostUnavailable, setProgramCostUnavailable] = useState(false);
	const [programCost, setProgramCost] = useState<number | null>(null);

	const { loading: programCostLoading } = usePricesByProgramQuery({
		context: { gqlServer: 'gateway' },
		skip: !showCost,
		variables: {
			academicPartnerUuid,
			programUuid,
			filters: [PriceFilter.ACTIVE],
		},
		onCompleted: data => {
			const standardPriceProgram = data?.pricesByProgram?.find(
				price => price.priceType.name === 'Guild Standard Price'
			);

			if (standardPriceProgram?.billingUnit !== BillingUnit.PERTERM || !standardPriceProgram) {
				setProgramCostUnavailable(true);
				setProgramCost(null);
			}

			setProgramCost(standardPriceProgram?.costPerUnit);
		},
		onError: error => {
			setProgramCostUnavailable(true);
			logDataDogError(error, 'PricesByProgramError');
		},
	});

	return (
		<PricebookContext.Provider
			value={{
				hideTuitionWhenNoOutOfPocket,
				programCost,
				programCostLoading,
				programCostUnavailable,
				showCost,
			}}
		>
			{children}
		</PricebookContext.Provider>
	);
};
